// @ts-check
class CarouselComponent extends HTMLElement {
  constructor() {
    super()
    this.init()
  }

  init() {
    this.selectors = {
      carousel: '.embla',
      currentSlideDisplay: '.slider-counter-current',
      emblaContainer: '.embla-container',
      next: '[name="next"]',
      previous: '[name="previous"]',
      pausePlay: '.slideshow__autoplay'
    }

    this.autoplayDefaults = {
      delay: 3000,
      // Hover / focus / interaction handling for autorotation is managed by this
      // component (see bindEvents) so it can follow the APG carousel pattern:
      // hover pause is transient, keyboard-focus pause is sticky.
      stopOnMouseEnter: false,
      stopOnInteraction: false
    }

    // other settings controlled in pseudo options
    this.defaults = {
      loop: true,
      speed: 10,
    }

    // Tracks the user's autorotation intent (true = should rotate while idle).
    // Flipped only by the play/pause button or by keyboard focus entering the carousel.
    this.autoplayActive = false
    this.reducedMotion = false

    this.loadCarousel = this.loadCarousel.bind(this)

    this.sliderSetup()
    this.updateSlideControlButtons()
    this.loadCarousel()

    if (Shopify.designMode) {
      this.setupThemeEditor()
    }
  }

  /**
   * destroy / reinitialize carousel when changes in theme editor
   * show slide block when a slide is chosen in sidebar
   */
  setupThemeEditor() {
    document.addEventListener('shopify:section:unload', ({ detail: { sectionId } }) => {
      if (sectionId == this.dataset.sectionId) {
        try {
          this.carousel.destroy()
        } catch (e) {
          console.log(e)
        }
      }
    })

    document.addEventListener('shopify:section:load', ({ detail: { sectionId } }) => {
      if (sectionId == this.dataset.sectionId) {
        this.setupCarousel()
      }
    })

    document.addEventListener('shopify:block:select', (event) => {
      const blockSelectedIsSlide = event.target.classList.contains('slideshow-slide');
      if (!blockSelectedIsSlide) return;

      this.pause();

      setTimeout(() => {
        this.carousel.scrollTo(Array.from(this.querySelector(this.selectors.emblaContainer).children).indexOf(event.target), true)
      }, 200);
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const blockDeselectedIsSlide = event.target.classList.contains('slideshow-slide');
      if (!blockDeselectedIsSlide) return;
      this.play();
    });
  }

  /**
   * A lot of legacy setup to accomodate methods that were copied over.
   */
  sliderSetup() {
    this.currentPage = 1
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.sliderControlWrapper = this.querySelector('.slider-buttons');

    if (this.sliderControlWrapper) {
      this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter-link'));
      this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
    }

    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
  }

  /**
   * Settings object to be consumed by embla
   *
   * @returns {Object}
   */
  getSettings() {
    const container = /** @type {HTMLElement} */ (this.querySelector(this.selectors.emblaContainer))
    const pseudoOptions = JSON.parse(getComputedStyle(this.querySelector(this.selectors.carousel), '::before').content.replace(/'/g, '"') || '{}')

    return {
      loop: pseudoOptions.loop ?? this.defaults.loop,
      autoplay: container.dataset.autoplay === 'true',
      autoplayOptions: {
        delay: parseInt(container.dataset.speed, 10) * 1000 || this.autoplayDefaults.delay,
        stopOnMouseEnter: this.autoplayDefaults.stopOnMouseEnter,
        stopOnInteraction: this.autoplayDefaults.stopOnInteraction
      }
    }
  }
  /**
   * wait until embla is available and then setup the carousel
   */
  loadCarousel() {
    document.removeEventListener('emblaLoad', this.loadCarousel)
    document.removeEventListener('emblaAutoplayLoad', this.loadCarousel)

    if (
      typeof window.EmblaCarousel !== 'undefined' &&
      typeof window.EmblaCarouselAutoplay !== 'undefined'
    ) {
      this.setupCarousel()
      return
    }

    document.addEventListener('emblaLoad', this.loadCarousel)
    document.addEventListener('emblaAutoplayLoad', this.loadCarousel)
  }

  setupCarousel() {
    const { autoplay, autoplayOptions, loop } = this.getSettings()

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const plugins = []
    if (autoplay) {
      // With reduced motion we still build the plugin (so the play button works)
      // but do not start it — APG-friendly and respects the OS setting.
      this.autoplay = EmblaCarouselAutoplay({ ...autoplayOptions, playOnInit: !this.reducedMotion })
      plugins.push(this.autoplay)
      this.autoplayActive = !this.reducedMotion
    }

    // sometimes not all the slides are in the DOM without waiting
    setTimeout(() => {
      this.initializeCarousel({ plugins, loop })
      this.bindEvents()
    }, 0)
  }

  initializeCarousel({ plugins, loop }) {
    this.carousel = EmblaCarousel(this.querySelector(this.selectors.carousel), { loop, containScroll: false }, plugins)
  }

  bindEvents() {
    const next = this.querySelector(this.selectors.next)
    const prev = this.querySelector(this.selectors.previous)
    this.liveRegion = this.querySelector(this.selectors.emblaContainer)
    this.sliderAutoplayButton = this.querySelector(this.selectors.pausePlay);

    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener('click', () => this.autoPlayToggle());
    }
    next.addEventListener('click', () => this.nextSlide())
    prev.addEventListener('click', () => this.previousSlide())

    this.carousel.on('select', this.onSlideChange.bind(this))
    this.onSlideChange()
    this.carousel.on('reInit', () => {
      this.setSlideVisibility()
      if (this.autoplay && this.autoplayActive && !this.reducedMotion) this.autoplay.play()
    })

    if (this.autoplay) {
      // APG: autorotation stops when keyboard focus enters the carousel and does
      // not restart until the user asks for it via the play/pause button.
      this.addEventListener('focusin', (event) => {
        if (this.sliderAutoplayButton && this.sliderAutoplayButton.contains(event.target)) return
        if (this.autoplayActive) this.pauseAutoplay({ sticky: true })
      })

      // APG: autorotation pauses while the pointer is over the carousel, then resumes.
      this.addEventListener('mouseenter', () => {
        if (this.autoplayActive) this.autoplay.stop()
      })
      this.addEventListener('mouseleave', () => {
        if (this.autoplayActive) this.autoplay.play()
      })

      // Insurance: the bundled autoplay plugin can restart its timer after a
      // pointer interaction; keep it stopped whenever the user has paused.
      this.carousel.on('pointerUp', () => {
        if (!this.autoplayActive) this.autoplay.stop()
      })
    }

    this.syncAutoplayState()

  // Allow clicks on slideshow-media-link only when not dragging
    let dragStartX = 0
    let isDragging = false

    this.querySelector(this.selectors.carousel).addEventListener('pointerdown', (e) => {
    dragStartX = e.clientX
    isDragging = false
   })

    this.querySelector(this.selectors.carousel).addEventListener('pointermove', (e) => {
   if (Math.abs(e.clientX - dragStartX) > 5) isDragging = true
})

this.querySelector(this.selectors.carousel).addEventListener('click', (e) => {
  const link = e.target.closest('.slideshow-media-link')
  if (link && isDragging) e.preventDefault()
}, true)
}
  nextSlide() {
    if (this.autoplay && this.autoplayActive) this.autoplay.reset()
    this.carousel.scrollNext()
  }

  previousSlide() {
    if (this.autoplay && this.autoplayActive) this.autoplay.reset()
    this.carousel.scrollPrev()
  }

  onSlideChange() {
    const previousPage = this.currentPage
    this.currentPage = this.carousel.selectedScrollSnap() + 1
    const currentPageDisplay = this.querySelector(this.selectors.currentSlideDisplay)

    if (currentPageDisplay) currentPageDisplay.textContent = this.currentPage

    this.updateSlideControlButtons()
    this.setSlideVisibility()

    // listened for by the media gallery component
    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1]
        }
      }));
    }
  }

  // Legacy methods / copied from Slider/Slideshow

  updateSlideControlButtons() {
    this.sliderControlButtons = this.querySelectorAll('.slider-counter-link');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter-link-active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter-link-active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  /**
   * Programmatic play/pause hooks kept for the theme editor. These are transient:
   * they never change the user's autorotation intent (this.autoplayActive).
   */
  play() {
    this.resumeAutoplay({ sticky: false })
  }

  pause() {
    this.pauseAutoplay({ sticky: false })
  }

  /**
   * Stop autorotation.
   * @param {{ sticky?: boolean }} [opts] sticky = the user asked to stop, so keep
   *   it stopped and reflect that in the button + live region.
   */
  pauseAutoplay({ sticky = true } = {}) {
    if (!this.autoplay) return
    this.autoplay.stop()
    if (sticky) {
      this.autoplayActive = false
      this.syncAutoplayState()
    }
  }

  /**
   * Start autorotation.
   * @param {{ sticky?: boolean }} [opts] sticky = the user asked to start it.
   */
  resumeAutoplay({ sticky = true } = {}) {
    if (!this.autoplay) return
    if (sticky) this.autoplayActive = true
    if (!this.autoplayActive) return
    this.autoplay.play()
    if (sticky) this.syncAutoplayState()
  }

  autoPlayToggle() {
    if (this.autoplayActive) {
      this.pauseAutoplay({ sticky: true })
    } else {
      this.resumeAutoplay({ sticky: true })
    }
  }

  /**
   * Reflect this.autoplayActive in the pause/play button label + state and in the
   * politeness of the slides live region (APG: "off" while rotating, "polite" when stopped).
   */
  syncAutoplayState() {
    if (this.liveRegion) {
      this.liveRegion.setAttribute('aria-live', this.autoplayActive ? 'off' : 'polite')
    }

    if (!this.sliderAutoplayButton) return
    const strings = window.accessibilityStrings || {}
    this.sliderAutoplayButton.classList.toggle('slideshow__autoplay--paused', !this.autoplayActive)
    this.sliderAutoplayButton.setAttribute('data-autoplay-state', this.autoplayActive ? 'playing' : 'paused')
    this.sliderAutoplayButton.setAttribute(
      'aria-label',
      this.autoplayActive ? strings.pauseSlideshow : strings.playSlideshow
    )
  }

  /**
   * Only the active slide stays interactive / in the a11y tree. Inactive slides get
   * `inert` so keyboard users cannot tab into off-screen links (APG requirement).
   */
  setSlideVisibility() {
    const activeIndex = this.currentPage - 1
    this.sliderItems.forEach((item, index) => {
      const isActive = index === activeIndex
      item.toggleAttribute('inert', !isActive)
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true')
      if (isActive) {
        item.removeAttribute('tabindex')
      } else {
        item.setAttribute('tabindex', '-1')
      }
    })
  }

  linkToSlide(event) {
    event.preventDefault();
    const newSlide = this.sliderControlLinksArray.indexOf(event.currentTarget)
    this.carousel.scrollTo(newSlide);
  }
}

customElements.define('carousel-component', CarouselComponent);
